import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ZoomIn, ZoomOut, RotateCw, Save, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { AccountNotFoundDialog } from "./AccountNotFoundDialog";
import { AccountMatchingService } from "@/services/AccountMatchingService";
import { useWorkspace } from "@/contexts/WorkspaceContext";

interface ManualReviewModalProps {
  bill: any;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const MANDATORY_FIELDS = ['account_number', 'bill_date', 'due_date', 'amount'];

export function ManualReviewModal({ bill, isOpen, onClose, onSave }: ManualReviewModalProps) {
  const { toast } = useToast();
  const { currentWorkspace } = useWorkspace();
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [parsedData, setParsedData] = useState<any>(bill?.extracted_data || {});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [similarAccounts, setSimilarAccounts] = useState<any[]>([]);

  useEffect(() => {
    if (bill?.extracted_data) {
      // Normalize legacy field names for backward compatibility
      const normalized = { ...bill.extracted_data };
      
      // Map amount_due to amount if needed (standardize on 'amount')
      if (normalized.amount_due && !normalized.amount) {
        normalized.amount = normalized.amount_due;
      }
      
      setParsedData(normalized);
    }
  }, [bill]);

  const updateField = (field: string, value: any) => {
    setParsedData((prev: any) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateFields = () => {
    const newErrors: Record<string, string> = {};
    
    MANDATORY_FIELDS.forEach((field) => {
      if (!parsedData[field] || parsedData[field].toString().trim() === '') {
        newErrors[field] = 'This field is required';
      }
    });

    if (parsedData.amount && parseFloat(parsedData.amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }

    if (parsedData.bill_date && parsedData.due_date) {
      const billDate = new Date(parsedData.bill_date);
      const dueDate = new Date(parsedData.due_date);
      if (dueDate < billDate) {
        newErrors.due_date = 'Due date must be after bill date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateFields()) {
      toast({
        title: "Validation Failed",
        description: "Please fill in all mandatory fields correctly",
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);

    try {
      // Check if account exists
      const { account } = await AccountMatchingService.findAccount(
        parsedData.account_number,
        currentWorkspace?.id || ''
      );

      if (!account && currentWorkspace?.id) {
        // Account not found - show dialog
        const similar = await AccountMatchingService.findSimilarAccounts(
          parsedData.account_number,
          currentWorkspace.id
        );
        setSimilarAccounts(similar);
        setShowAccountDialog(true);
        setIsSaving(false);
        return;
      }

      // Account found or created - proceed with save
      await completeSave(account?.id);
    } catch (error: any) {
      toast({
        title: "Save Error",
        description: error.message,
        variant: "destructive"
      });
      setIsSaving(false);
    }
  };

  const completeSave = async (accountId?: string) => {
    try {
      const reviewStartTime = bill.claimed_at ? new Date(bill.claimed_at) : new Date();
      const reviewEndTime = new Date();
      const durationSeconds = Math.floor((reviewEndTime.getTime() - reviewStartTime.getTime()) / 1000);
      const now = new Date().toISOString();

      // Update queue item with proper stage transition fields
      const { error: queueError } = await supabase
        .from('bill_upload_queue')
        .update({
          extracted_data: parsedData,
          assigned_to_account_id: accountId,
          assignment_status: accountId ? 'manually_assigned' : 'unassigned',
          processing_status: 'completed',
          processing_completed_at: now, // Critical for validation queue filter
          reviewed_at: now,
          review_duration_seconds: durationSeconds
        } as any)
        .eq('id', bill.id);

      if (queueError) throw queueError;

      // Update bills table for reporting (non-fatal if fails)
      if (bill.bill_id) {
        const { error: billError } = await supabase
          .from('bills')
          .update({
            reviewed_by: bill.user_id,
            reviewed_at: now,
            review_duration_seconds: durationSeconds,
            updated_at: now,
          })
          .eq('id', bill.bill_id);

        if (billError) {
          console.warn('Failed to update bill record:', billError);
        }
      }

      toast({
        title: "Review Saved",
        description: "Bill has been reviewed and moved to validation",
      });

      onSave();
      onClose();
    } catch (error: any) {
      toast({
        title: "Save Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendToOnboarding = async () => {
    if (!currentWorkspace?.id) return;

    try {
      await AccountMatchingService.createOnboardingRequest({
        workspaceId: currentWorkspace.id,
        billId: bill.id,
        queueItemId: bill.id,
        rawAccountNumber: parsedData.account_number,
        rawCustomerName: parsedData.customer_name,
        createdBy: bill.user_id
      });

      // Update bill to dedicated pending_onboarding status (not generic 'pending')
      await supabase
        .from('bill_upload_queue')
        .update({ 
          processing_status: 'pending_onboarding' as any,
          manual_review_required: false, // Remove from manual review queue
          updated_at: new Date().toISOString()
        })
        .eq('id', bill.id);

      toast({
        title: "Sent to Onboarding",
        description: "Account creation request has been created"
      });

      setShowAccountDialog(false);
      onClose();
    } catch (error: any) {
      toast({
        title: "Failed",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const getFieldConfidence = (field: string) => {
    return bill?.per_field_confidence?.[field] || 0;
  };

  const ConfidenceBadge = ({ field }: { field: string }) => {
    const confidence = getFieldConfidence(field);
    const percentage = Math.round(confidence * 100);
    
    let colorClass = "bg-green-100 dark:bg-green-950/20 text-green-800 dark:text-green-400";
    let emoji = "🟢";
    
    if (confidence >= 0.95) {
      colorClass = "bg-green-100 dark:bg-green-950/20 text-green-800 dark:text-green-400";
      emoji = "🟢";
    } else if (confidence >= 0.85) {
      colorClass = "bg-yellow-100 dark:bg-yellow-950/20 text-yellow-800 dark:text-yellow-400";
      emoji = "🟡";
    } else {
      colorClass = "bg-red-100 dark:bg-red-950/20 text-red-800 dark:text-red-400";
      emoji = "🔴";
    }
    
    return (
      <Badge variant="outline" className={colorClass}>
        {emoji} {percentage}%
      </Badge>
    );
  };

  const missingMandatoryFields = MANDATORY_FIELDS.filter(
    field => !parsedData[field] || parsedData[field].toString().trim() === ''
  );

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-screen-2xl h-[95vh] p-0">
          <div className="flex h-full">
            
            {/* LEFT: PDF Viewer (50%) */}
            <div className="w-1/2 border-r bg-muted/30 flex flex-col">
              {/* PDF Toolbar */}
              <div className="flex items-center justify-between p-4 border-b bg-background">
                <h3 className="font-semibold">{bill.file_name}</h3>
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setZoom(Math.max(50, zoom - 10))}
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="text-sm min-w-[50px] text-center">{zoom}%</span>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setZoom(Math.min(200, zoom + 10))}
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setRotation((r) => (r + 90) % 360)}
                  >
                    <RotateCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* PDF Display */}
              <div className="flex-1 flex items-center justify-center p-4 overflow-auto bg-muted">
                {bill?.file_path ? (
                  <iframe
                    src={bill.file_path}
                    className="w-full h-full border-0 rounded bg-background shadow"
                    style={{ 
                      transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                      transformOrigin: 'center',
                      transition: 'transform 0.2s'
                    }}
                  />
                ) : (
                  <div className="text-center text-muted-foreground">
                    <p>No preview available</p>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT: Form Fields (50%) */}
            <div className="w-1/2 flex flex-col">
              {/* Form Header */}
              <div className="p-4 border-b bg-background">
                <h3 className="font-semibold">Review & Edit Bill Data</h3>
                <p className="text-sm text-muted-foreground">
                  Verify extracted information and fill in any missing fields
                </p>
                
                {missingMandatoryFields.length > 0 && (
                  <Alert className="mt-3">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      {missingMandatoryFields.length} mandatory field(s) missing: {missingMandatoryFields.join(', ')}
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Form Fields */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4 max-w-2xl">
                  {/* Account Number */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="account_number">
                        Account Number <span className="text-red-500">*</span>
                      </Label>
                      <ConfidenceBadge field="account_number" />
                    </div>
                    <Input
                      id="account_number"
                      value={parsedData.account_number || ''}
                      onChange={(e) => updateField('account_number', e.target.value)}
                      className={errors.account_number ? 'border-red-500' : ''}
                    />
                    {errors.account_number && (
                      <p className="text-sm text-red-500">{errors.account_number}</p>
                    )}
                  </div>

                  {/* Bill Date */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="bill_date">
                        Bill Date <span className="text-red-500">*</span>
                      </Label>
                      <ConfidenceBadge field="bill_date" />
                    </div>
                    <Input
                      id="bill_date"
                      type="date"
                      value={parsedData.bill_date || ''}
                      onChange={(e) => updateField('bill_date', e.target.value)}
                      className={errors.bill_date ? 'border-red-500' : ''}
                    />
                    {errors.bill_date && (
                      <p className="text-sm text-red-500">{errors.bill_date}</p>
                    )}
                  </div>

                  {/* Due Date */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="due_date">
                        Due Date <span className="text-red-500">*</span>
                      </Label>
                      <ConfidenceBadge field="due_date" />
                    </div>
                    <Input
                      id="due_date"
                      type="date"
                      value={parsedData.due_date || ''}
                      onChange={(e) => updateField('due_date', e.target.value)}
                      className={errors.due_date ? 'border-red-500' : ''}
                    />
                    {errors.due_date && (
                      <p className="text-sm text-red-500">{errors.due_date}</p>
                    )}
                  </div>

                  {/* Amount */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="amount">
                        Amount Due <span className="text-red-500">*</span>
                      </Label>
                      <ConfidenceBadge field="amount" />
                    </div>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={parsedData.amount || ''}
                      onChange={(e) => updateField('amount', e.target.value)}
                      className={errors.amount ? 'border-red-500' : ''}
                    />
                    {errors.amount && (
                      <p className="text-sm text-red-500">{errors.amount}</p>
                    )}
                  </div>

                  {/* Customer Name */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="customer_name">Customer Name</Label>
                      <ConfidenceBadge field="customer_name" />
                    </div>
                    <Input
                      id="customer_name"
                      value={parsedData.customer_name || ''}
                      onChange={(e) => updateField('customer_name', e.target.value)}
                    />
                  </div>

                  {/* Meter Number */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="meter_number">Meter Number</Label>
                      <ConfidenceBadge field="meter_number" />
                    </div>
                    <Input
                      id="meter_number"
                      value={parsedData.meter_number || ''}
                      onChange={(e) => updateField('meter_number', e.target.value)}
                    />
                  </div>
                </div>
              </ScrollArea>

              {/* Form Actions */}
              <div className="p-4 border-t bg-background flex justify-end gap-2">
                <Button variant="outline" onClick={onClose} disabled={isSaving}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save & Continue'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AccountNotFoundDialog
        isOpen={showAccountDialog}
        accountNumber={parsedData.account_number}
        billId={bill.id}
        similarAccounts={similarAccounts}
        onSendToOnboarding={handleSendToOnboarding}
        onUseSimilarAccount={async (accountId) => {
          setShowAccountDialog(false);
          await completeSave(accountId);
        }}
        onCancel={() => setShowAccountDialog(false)}
      />
    </>
  );
}
